import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  initializePaddle,
  CheckoutEventNames,
  type Paddle,
  type PaddleEventData,
  type Environments,
} from '@paddle/paddle-js';
import { useAuth } from '../auth/AuthContext.js';
import {
  getCheckoutContext,
  getSubscriptionMe,
  type BillingInterval,
  type CheckoutContext,
  type SubscriptionMe,
  type SubscriptionTier,
} from '../api/subscriptions.js';

/**
 * Paddle.js integration for the self-serve checkout overlay (ADR-0026 PR-3b-3).
 *
 * The pure functions below carry the load-bearing logic and are unit-tested in
 * isolation (no Paddle, no DOM):
 *   - buildCheckoutOpenArgs — pins `customData: { subscriptionId }` VERBATIM (the
 *     single most dangerous line: the webhook reads data.custom_data.subscriptionId
 *     with the inner key preserved un-snake-cased; any rename → paid checkout never
 *     links, silently).
 *   - resolveSnapBack — the under-buy defense (Q0 verdict ii). A tenant can't buy
 *     fewer seats than their headcount; the overlay stepper is snapped back.
 *   - subscriptionKey / didFlip — the confirming-poll's flip detector.
 *
 * The `usePaddleCheckout` hook wires them to Paddle.js + react-query.
 */

// ─── Env (fail-loud; NOT the app's silent `?? ''` — a payment token must not be empty) ───

/** The publishable client-side token. Absent/empty ⇒ checkout is disabled (fail-loud). */
export function getPaddleToken(): string {
  return (import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined)?.trim() ?? '';
}

export function isPaddleConfigured(): boolean {
  return getPaddleToken().length > 0;
}

/** Sandbox unless VITE_PADDLE_ENV=production. Read from env, never hardcoded. */
export function getPaddleEnv(): Environments {
  return (import.meta.env.VITE_PADDLE_ENV as string | undefined) === 'production' ? 'production' : 'sandbox';
}

// ─── Pure logic ───

/** The exact arguments passed to Paddle.Checkout.open(). */
export interface CheckoutOpenArgs {
  items: { priceId: string; quantity: number }[];
  customData: { subscriptionId: string };
  customer?: { email: string };
  settings: { displayMode: 'overlay' };
}

/**
 * Build the Checkout.open() arguments. `customData.subscriptionId` is the literal
 * key the webhook links on — pinned by a unit test; do NOT rename or snake-case it.
 */
export function buildCheckoutOpenArgs(ctx: CheckoutContext, email?: string): CheckoutOpenArgs {
  return {
    items: [{ priceId: ctx.priceId, quantity: ctx.quantity }],
    customData: { subscriptionId: ctx.subscriptionId },
    ...(email ? { customer: { email } } : {}),
    settings: { displayMode: 'overlay' },
  };
}

/**
 * The under-buy snap-back decision. `headcount` is the tenant's current seat count
 * (checkout-context's quantity). Buying below it would let them pay for fewer seats
 * than they occupy (the drift Model C prevents) — so snap back up. Buying at or
 * above is honored (headroom). The loop guard: only reset when strictly below, so
 * resetting to exactly headcount produces an `== headcount` event and stops.
 */
export function resolveSnapBack(
  currentQty: number,
  headcount: number,
): { reset: boolean; quantity: number; warn: boolean } {
  if (currentQty < headcount) return { reset: true, quantity: headcount, warn: true };
  return { reset: false, quantity: currentQty, warn: false };
}

export function underBuyWarning(headcount: number): string {
  return `You have ${headcount} employees — you can't buy fewer than ${headcount} seats.`;
}

/**
 * A stable key over the fields the webhook changes on a successful checkout:
 * status/tier flip (trial→active, tier change) and the seat cap (buy-more-seats).
 * A change in this key ⇒ the webhook landed.
 */
export function subscriptionKey(me: Pick<SubscriptionMe, 'effectiveStatus' | 'tier' | 'employeeCap'> | undefined | null): string {
  if (!me) return '';
  return `${me.effectiveStatus}|${me.tier}|${me.employeeCap ?? 'null'}`;
}

/** True once /subscriptions/me differs from the snapshot taken when confirming began. */
export function didFlip(snapshotKey: string, current: SubscriptionMe | undefined): boolean {
  if (!current) return false;
  const currentKey = subscriptionKey(current);
  return currentKey !== '' && snapshotKey !== '' && currentKey !== snapshotKey;
}

// ─── The hook ───

export type CheckoutPhase = 'idle' | 'opening' | 'confirming' | 'success' | 'timeout' | 'error';

const CONFIRM_TIMEOUT_MS = 25_000;

export interface UsePaddleCheckout {
  configured: boolean;
  phase: CheckoutPhase;
  warning: string | null;
  openCheckout: (tier: SubscriptionTier, interval: BillingInterval) => Promise<void>;
  dismiss: () => void;
}

export function usePaddleCheckout(): UsePaddleCheckout {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const configured = isPaddleConfigured();

  const paddleRef = useRef<Paddle | null>(null);
  const activeRef = useRef<{ priceId: string; headcount: number } | null>(null);
  const snapshotRef = useRef<string>('');
  const [phase, setPhase] = useState<CheckoutPhase>('idle');
  const [warning, setWarning] = useState<string | null>(null);

  // The event handler lives in a ref so the callback registered once at
  // initialize always calls the latest logic (no stale closure).
  const handlerRef = useRef<(ev: PaddleEventData) => void>(() => {});
  handlerRef.current = (ev: PaddleEventData): void => {
    switch (ev.name) {
      case CheckoutEventNames.CHECKOUT_UPDATED:
      case CheckoutEventNames.CHECKOUT_ITEMS_UPDATED: {
        const active = activeRef.current;
        const qty = ev.data?.items?.[0]?.quantity;
        if (!active || typeof qty !== 'number') return;
        const sb = resolveSnapBack(qty, active.headcount);
        setWarning(sb.warn ? underBuyWarning(active.headcount) : null);
        if (sb.reset) paddleRef.current?.Checkout.updateItems([{ priceId: active.priceId, quantity: sb.quantity }]);
        break;
      }
      case CheckoutEventNames.CHECKOUT_COMPLETED:
        // Paddle took the money — assert NO subscription state. Snapshot /me and
        // enter 'confirming'; the poll on /subscriptions/me is the ONLY source of
        // truth for whether OUR system recorded the activation (the webhook).
        snapshotRef.current = subscriptionKey(queryClient.getQueryData<SubscriptionMe>(['subscription', 'me']));
        setWarning(null);
        setPhase('confirming');
        break;
      case CheckoutEventNames.CHECKOUT_CLOSED:
        // Abandoned — return to pre-checkout state, no false "confirming". (Closed
        // also fires after completed; don't clobber an in-flight confirm/success.)
        activeRef.current = null;
        setWarning(null);
        setPhase((p) => (p === 'confirming' || p === 'success' ? p : 'idle'));
        break;
      default:
        break;
    }
  };

  // Initialize Paddle.js once (only when configured — never with an empty token).
  useEffect(() => {
    if (!configured) return;
    let alive = true;
    void initializePaddle({
      environment: getPaddleEnv(),
      token: getPaddleToken(),
      eventCallback: (ev) => handlerRef.current(ev),
    }).then((p) => {
      if (alive && p) paddleRef.current = p;
    });
    return () => {
      alive = false;
    };
  }, [configured]);

  // The confirming-poll: refetch /me every 2s while confirming; flip to success
  // when it differs from the snapshot (the webhook landed).
  const poll = useQuery({
    queryKey: ['subscription', 'me'],
    queryFn: getSubscriptionMe,
    refetchInterval: phase === 'confirming' ? 2000 : false,
    enabled: phase === 'confirming',
  });

  useEffect(() => {
    if (phase !== 'confirming') return;
    if (didFlip(snapshotRef.current, poll.data)) {
      setPhase('success');
      void queryClient.invalidateQueries({ queryKey: ['subscription', 'me'] });
    }
  }, [phase, poll.data, queryClient]);

  // Timeout fallback — webhook lag. Never assert success from the callback alone.
  useEffect(() => {
    if (phase !== 'confirming') return;
    const t = setTimeout(() => setPhase((p) => (p === 'confirming' ? 'timeout' : p)), CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  const openCheckout = useCallback(
    async (tier: SubscriptionTier, interval: BillingInterval): Promise<void> => {
      const paddle = paddleRef.current;
      if (!paddle || !configured) return;
      setWarning(null);
      setPhase('opening');
      try {
        const ctx = await getCheckoutContext(tier, interval);
        activeRef.current = { priceId: ctx.priceId, headcount: ctx.quantity };
        paddle.Checkout.open(buildCheckoutOpenArgs(ctx, user?.email));
        setPhase('idle'); // the overlay is open; the eventCallback drives what's next
      } catch {
        setPhase('error');
      }
    },
    [configured, user],
  );

  const dismiss = useCallback(() => {
    setPhase('idle');
    setWarning(null);
    activeRef.current = null;
  }, []);

  return { configured, phase, warning, openCheckout, dismiss };
}
